/* Chi tiết một bài nộp: nội dung, vote, bình luận. */
import { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Textarea } from "@/components/ui/textarea"
import { Icon } from "@/components/icon"
import { EmptyState, LoadingState, SectionTitle, StatusNote } from "@/components/page"
import { api, post } from "@/lib/api"
import { formatDateTime } from "@/lib/format"
import { cn } from "@/lib/utils"
import { MeTag, StatusBadge, SubmissionAvatar } from "./shared"

const signed = (n: number) => (n > 0 ? `+${n}` : String(n ?? 0))

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1 rounded-2xl bg-paper p-4">
      <dt className="text-sm font-semibold text-muted-foreground">{label}</dt>
      <dd className="text-base break-words whitespace-pre-wrap text-ink">{children}</dd>
    </div>
  )
}

function Comment({ comment }: { comment: any }) {
  return (
    <li className="flex gap-3">
      <SubmissionAvatar author={comment.author} size="sm" />
      <div className="flex min-w-0 flex-1 flex-col gap-1 rounded-2xl bg-paper px-4 py-3">
        <div className="flex flex-wrap items-center gap-2">
          <strong className="text-sm text-ink">{comment.author?.full_name}</strong>
          <MeTag show={comment.author?.is_me} />
          <span className="text-sm text-muted-foreground">{formatDateTime(comment.created_at)}</span>
        </div>
        <p className="text-sm break-words whitespace-pre-wrap text-ink">{comment.body}</p>
      </div>
    </li>
  )
}

export function SubmissionDetail({ slug, id, backTo }: { slug: string; id: string; backTo: string }) {
  const [detail, setDetail] = useState<any>()
  const [loading, setLoading] = useState(true)
  const [voteBusy, setVoteBusy] = useState(false)
  const [commentBody, setCommentBody] = useState("")
  const [commentBusy, setCommentBusy] = useState(false)
  const [message, setMessage] = useState<{ tone: "success" | "error"; text: string } | null>(null)
  const base = `/api/classes/${encodeURIComponent(slug)}/submissions/${encodeURIComponent(id)}`

  useEffect(() => {
    setDetail(undefined)
    setMessage(null)
    setCommentBody("")
    setLoading(true)
    api(base)
      .then(setDetail)
      .catch((e: any) => setMessage({ tone: "error", text: e.message }))
      .finally(() => setLoading(false))
    window.scrollTo({ top: 0, behavior: "smooth" })
  }, [base])

  const vote = async (value: number) => {
    if (!detail || voteBusy) return
    const next = detail.my_vote === value ? 0 : value
    setVoteBusy(true)
    setMessage(null)
    try {
      const r: any = await post(`${base}/vote`, { value: next })
      setDetail((c: any) => (c ? { ...c, score: r.score, my_vote: r.my_vote, vote_count: r.vote_count } : c))
    } catch (e: any) {
      setMessage({ tone: "error", text: e.message })
    } finally {
      setVoteBusy(false)
    }
  }

  const submitComment = async () => {
    const body = commentBody.trim()
    if (!body) return
    setCommentBusy(true)
    setMessage(null)
    try {
      const r: any = await post(`${base}/comments`, { body })
      setDetail((c: any) => (c ? { ...c, comments: [...(c.comments || []), r.comment] } : c))
      setCommentBody("")
      if (r.badge_awarded) setMessage({ tone: "success", text: `Con nhận badge “${r.badge_awarded}” cho việc góp ý cho bạn!` })
    } catch (e: any) {
      setMessage({ tone: "error", text: e.message })
    } finally {
      setCommentBusy(false)
    }
  }

  const back = (
    <Button variant="outline" size="sm" className="self-start" nativeButton={false} render={<Link to={backTo} />}>
      <Icon name="arrow-left" className="size-4" /> Bài của lớp
    </Button>
  )

  if (loading) return <>{back}<LoadingState label="Đang mở bài nộp…" /></>
  const s = detail?.submission
  if (!s)
    return (
      <>
        {back}
        <EmptyState mascot="meerkat-thinking" title="Không tìm thấy bài nộp" description={message?.text} />
      </>
    )
  const comments: any[] = detail.comments || []

  return (
    <>
      {back}
      <Card>
        <CardContent className="flex flex-col gap-5">
          <header className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-3">
              <SubmissionAvatar author={s.author} size="lg" />
              <div className="flex min-w-0 flex-col">
                <span className="flex flex-wrap items-center gap-2">
                  <strong className="text-base text-ink">{s.author?.full_name}</strong>
                  <MeTag show={s.author?.is_me} />
                </span>
                <span className="text-sm text-muted-foreground">{formatDateTime(s.updated_at || s.created_at)}</span>
              </div>
            </div>
            <StatusBadge status={s.status} />
          </header>
          <div className="flex flex-col gap-1">
            <h1 className="text-2xl leading-tight font-bold sm:text-3xl">{s.product_title || s.product_name}</h1>
            <p className="text-sm text-muted-foreground">
              {s.product_title && <>{s.product_name} · </>}Phiên bản {s.version}
            </p>
          </div>
          <dl className="grid gap-3 sm:grid-cols-2">
            {s.artifact_url && (
              <div className="sm:col-span-2">
                <Field label="Link sản phẩm">
                  <a href={s.artifact_url} target="_blank" rel="noreferrer" className="inline-flex max-w-full items-center gap-2 font-semibold text-orange-strong underline-offset-4 hover:underline">
                    <Icon name="globe" className="size-4" />
                    <span className="truncate">{s.artifact_url}</span>
                  </a>
                </Field>
              </div>
            )}
            {s.audience && <Field label="Đối tượng">{s.audience}</Field>}
            {s.audience_need && <Field label="Nhu cầu của đối tượng">{s.audience_need}</Field>}
            {s.solution && (
              <div className="sm:col-span-2">
                <Field label="Giải pháp">{s.solution}</Field>
              </div>
            )}
          </dl>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-1 rounded-full bg-soft p-1">
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label="Upvote"
                aria-pressed={detail.my_vote === 1}
                disabled={voteBusy}
                onClick={() => vote(1)}
                className={cn(detail.my_vote === 1 && "bg-orange text-white hover:bg-orange hover:text-white")}
              >
                <Icon name="chevron-up" className="size-5" />
              </Button>
              <b className="min-w-8 text-center font-heading text-lg text-ink">{signed(detail.score)}</b>
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label="Downvote"
                aria-pressed={detail.my_vote === -1}
                disabled={voteBusy}
                onClick={() => vote(-1)}
                className={cn(detail.my_vote === -1 && "bg-ink text-white hover:bg-ink hover:text-white")}
              >
                <Icon name="chevron-down" className="size-5" />
              </Button>
            </div>
            <span className="text-sm text-muted-foreground">{detail.vote_count || 0} lượt vote</span>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="flex flex-col gap-4">
          <SectionTitle title={`Bình luận (${comments.length})`} />
          {comments.length ? (
            <ul className="flex flex-col gap-3">
              {comments.map((c) => (
                <Comment key={c.id} comment={c} />
              ))}
            </ul>
          ) : (
            <p className="text-sm text-muted-foreground">Chưa có bình luận. Hãy là người đầu tiên góp ý cho bạn.</p>
          )}
          <div className="flex flex-col gap-3">
            <Textarea
              aria-label="Bình luận"
              maxLength={2000}
              rows={3}
              value={commentBody}
              onChange={(e) => setCommentBody(e.target.value)}
              placeholder="Viết bình luận cho bài này…"
              className="text-base"
            />
            <Button className="self-end" disabled={commentBusy || !commentBody.trim()} onClick={submitComment}>
              {commentBusy ? "Đang gửi…" : "Gửi bình luận"} <Icon name="send" className="size-4" />
            </Button>
          </div>
          <StatusNote tone={message?.tone}>{message?.text}</StatusNote>
        </CardContent>
      </Card>
    </>
  )
}
